/* PALISADE CUSTOM — submits the wrapped Shopify {% form %} without reloading the page. */

class PalisadeAjaxForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    // Once bound, Shopify's hCaptcha has already captured the native submit, so stay native.
    if (!this.form || this.form.hasAttribute('data-hcaptcha-bound')) return;

    // Shopify's hCaptcha captures form.submit when it binds, then calls it with the token attached.
    this.form.submit = () => this.send();
    this.onSubmit = this.onSubmit.bind(this);
    window.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    if (this.onSubmit) window.removeEventListener('submit', this.onSubmit);
  }

  // Window listeners run last: a submit nobody cancelled is not captcha-protected.
  onSubmit(event) {
    if (event.target !== this.form || event.defaultPrevented) return;
    event.preventDefault();
    this.send();
  }

  async send() {
    if (this.busy) return;
    this.setBusy(true);

    let response;
    try {
      response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        credentials: 'same-origin',
      });
    } catch {
      this.fallback();
      return;
    }

    const url = new URL(response.url);
    const posted = url.searchParams.has('contact_posted') || url.searchParams.has('customer_posted');
    const challenged = url.pathname.startsWith('/challenge');
    const fresh = response.ok && !challenged ? this.findForm(await response.text()) : null;

    if (fresh) {
      this.form.replaceChildren(...fresh.childNodes);
      this.setBusy(false);
      this.focusStatus();
    } else if (posted) {
      // Already accepted: show Shopify's result page rather than posting twice.
      window.location.assign(response.url);
    } else {
      this.fallback();
    }
  }

  findForm(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = this.closest('.shopify-section');
    const scope = (section && doc.getElementById(section.id)) || doc;
    return this.form.id ? scope.querySelector(`#${CSS.escape(this.form.id)}`) : null;
  }

  focusStatus() {
    const status = this.form.querySelector('[autofocus], [role="alert"], [role="status"]');
    if (!status) return;
    if (!status.hasAttribute('tabindex')) status.setAttribute('tabindex', '-1');
    status.focus();
  }

  // aria-disabled, not disabled: a disabled button drops focus to <body> mid-request.
  setBusy(busy) {
    this.busy = busy;
    this.form.querySelectorAll('[type="submit"]').forEach((button) => {
      if (busy) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    });
  }

  fallback() {
    this.setBusy(false);
    HTMLFormElement.prototype.submit.call(this.form);
  }
}

if (!customElements.get('palisade-ajax-form')) {
  customElements.define('palisade-ajax-form', PalisadeAjaxForm);
}
